import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { SlidersHorizontal, Star, Heart, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    listing_type: '',
    property_type: '',
    city: '',
    min_price: '',
    max_price: '',
    bedrooms: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlFilters = {
      listing_type: params.get('listing_type') || '',
      property_type: params.get('property_type') || '',
      city: params.get('city') || '',
      state: params.get('state') || '',
      min_price: params.get('min_price') || '',
      max_price: params.get('max_price') || '',
      bedrooms: params.get('bedrooms') || ''
    };
    setFilters(urlFilters);
    fetchProperties(urlFilters);
  }, []);

  const fetchProperties = async (filterParams = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filterParams).forEach(key => {
        if (filterParams[key]) {
          params.append(key, filterParams[key]);
        }
      });

      const response = await axios.get(`${API}/properties?${params.toString()}`);
      setProperties(response.data);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
  };

  const applyFilters = () => {
    fetchProperties(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      listing_type: '',
      property_type: '',
      city: '',
      min_price: '',
      max_price: '',
      bedrooms: ''
    };
    setFilters(emptyFilters);
    fetchProperties(emptyFilters);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="properties-page">
      {/* Header with Filters - Airbnb Style */}
      <div className="sticky top-[160px] bg-white border-b border-gray-200 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900" data-testid="properties-title">
                Stays in California & Arizona
              </h1>
              <p className="text-sm text-gray-600 mt-1">{properties.length} homes</p>
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-300 rounded-lg hover:border-blue-900 transition-colors"
              data-testid="filters-button"
            >
              <SlidersHorizontal className="h-4 w-4" />
              <span className="font-semibold">Filters</span>
            </button>
          </div>

          {/* Filter Modal */}
          {showFilters && (
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowFilters(false)}>
              <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl font-semibold">Filters</h2>
                    <button onClick={() => setShowFilters(false)} className="text-gray-600 hover:text-gray-900">✕</button>
                  </div>

                  <div className="space-y-8">
                    {/* Price Range */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Price range</h3>
                      <p className="text-sm text-gray-600 mb-4">Nightly prices before fees and taxes</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-600">Minimum</label>
                          <input
                            type="number"
                            placeholder="$50"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg mt-2"
                            value={filters.min_price}
                            onChange={(e) => handleFilterChange('min_price', e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm text-gray-600">Maximum</label>
                          <input
                            type="number"
                            placeholder="$500"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg mt-2"
                            value={filters.max_price}
                            onChange={(e) => handleFilterChange('max_price', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Property Type */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Type of place</h3>
                      <div className="grid grid-cols-2 gap-3">
                        {['house', 'apartment', 'villa', 'condo'].map((type) => (
                          <button
                            key={type}
                            onClick={() => handleFilterChange('property_type', filters.property_type === type ? '' : type)}
                            className={`px-4 py-3 border rounded-lg text-left capitalize transition-colors ${
                              filters.property_type === type
                                ? 'border-blue-900 bg-blue-50'
                                : 'border-gray-300 hover:border-blue-900'
                            }`}
                          >
                            {type}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bedrooms */}
                    <div>
                      <h3 className="text-lg font-semibold mb-4">Bedrooms</h3>
                      <div className="flex gap-2">
                        {['Any', '1', '2', '3', '4', '5+'].map((num) => (
                          <button
                            key={num}
                            onClick={() => handleFilterChange('bedrooms', num === 'Any' ? '' : num.replace('+', ''))}
                            className={`px-6 py-3 border rounded-full transition-colors ${
                              (num === 'Any' && !filters.bedrooms) || filters.bedrooms === num.replace('+', '')
                                ? 'border-blue-900 bg-blue-50'
                                : 'border-gray-300 hover:border-blue-900'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 mt-8 flex items-center justify-between">
                    <button
                      onClick={clearFilters}
                      className="text-sm font-semibold underline"
                    >
                      Clear all
                    </button>
                    <button
                      onClick={applyFilters}
                      className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-lg font-semibold"
                    >
                      Show {properties.length} places
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Properties Grid - Airbnb Style */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-gray-600 mb-4">No exact matches</p>
            <p className="text-gray-600">Try adjusting your search or filters to find what you're looking for.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {properties.map((property) => (
              <Link
                key={property.id}
                to={`/property/${property.id}`}
                className="group"
                data-testid={`property-card-${property.id}`}
              >
                <div className="relative mb-3">
                  <div className="aspect-square rounded-xl overflow-hidden">
                    <img
                      src={property.images[0]}
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <button className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white hover:scale-110 transition-all">
                    <Heart className="h-4 w-4 text-gray-700" />
                  </button>
                  {property.featured && (
                    <div className="absolute top-3 left-3 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Guest favorite
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 group-hover:underline">{property.city}, {property.state}</h3>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-600 text-yellow-600" />
                      <span className="text-sm font-semibold">4.9</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{property.bedrooms} bedrooms · {property.bathrooms} baths</p>
                  <p className="text-sm text-gray-600 mb-2">Available now</p>
                  <div>
                    <span className="font-semibold text-gray-900">${property.price}</span>
                    <span className="text-gray-600"> night</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Properties;
